import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { EventClickArg, EventContentArg, DatesSetArg, EventInput } from '@fullcalendar/core';
import esLocale from '@fullcalendar/core/locales/es';
import useIsMobile from '../../services/hooks/useIsMobile';

export interface BaseCalendarProps {
  events: EventInput[];
  /** Custom renderer for each event block. */
  eventContent?: (arg: EventContentArg) => React.ReactNode;
  onEventClick?: (arg: EventClickArg) => void;
  onDateClick?: (arg: DateClickArg) => void;
  onDatesSet?: (arg: DatesSetArg) => void;
  /** Admin calendars set these true to create/move events; client view leaves them false. */
  editable?: boolean;
  selectable?: boolean;
  initialView?: string;
  dayMaxEvents?: number;
  eventMaxStack?: number;
  /**
   * Limita a qué fechas se puede navegar (FullCalendar desactiva prev/next al
   * llegar al borde). `end` es EXCLUSIVO. El calendario del cliente lo usa para
   * no dejarle pasear por meses que el centro no le muestra: sin esto vería un
   * mes vacío y pensaría que no hay clases.
   */
  validRange?: { start?: Date; end?: Date };
}

// Shared look & feel for every FullCalendar in the app. Keep visual tweaks here
// so the admin and client calendars stay consistent.
const calendarWrapperClass = `[&_.fc]:font-sans [&_.fc-toolbar-title]:text-lg [&_.fc-toolbar-title]:font-bold [&_.fc-toolbar-title]:text-gray-800
  [&_.fc-button]:bg-blue-500 [&_.fc-button]:text-sm [&_.fc-button]:hover:bg-blue-600 [&_.fc-button]:transition-colors [&_.fc-button]:duration-200
  [&_.fc-button]:border-0 [&_.fc-button]:rounded [&_.fc-button]:px-4 [&_.fc-button]:py-2 [&_.fc-button]:text-white
  [&_.fc-event]:bg-transparent [&_.fc-event]:border-transparent [&_.fc-event]:rounded [&_.fc-event]:cursor-pointer [&_.fc-event-title]:text-white
  [&_.fc-day-today]:bg-blue-50
  [&_.fc-timegrid-slot]:h-14 [&_.fc-timegrid-slot-label]:text-xs [&_.fc-timegrid-slot-label]:text-gray-600
  [&_.fc-event]:my-1
  [&_.fc-col-header-cell]:bg-gray-50 [&_.fc-col-header-cell]:py-1 [&_.fc-col-header-cell]:text-sm
  [&_.fc-popover]:!z-50 [&_.fc-popover]:max-h-96 [&_.fc-popover]:overflow-y-auto
  [&_.fc-popover-body]:max-h-80 [&_.fc-popover]:!bg-white [&_.fc-popover]:shadow-xl
  [&_.fc-popover-header]:bg-gray-100 [&_.fc-popover-header]:p-2
  [&_.fc-more-popover]:!absolute font-bold`;

// D4 — la misma rejilla, encogida para 375px: menos margen en las celdas, franja
// horaria estrecha, franjas más bajas (14h de 08:00 a 22:00 no caben de otro
// modo) y la barra de botones en dos filas en vez de salirse por la derecha.
const mobileTweaksClass = `[&_.fc-toolbar]:flex-wrap [&_.fc-toolbar]:gap-2 [&_.fc-toolbar-title]:!text-base
  [&_.fc-button]:!px-2 [&_.fc-button]:!py-1 [&_.fc-button]:!text-xs
  [&_.fc-timegrid-slot]:!h-8
  [&_.fc-timegrid-slot-label]:!text-[10px] [&_.fc-timegrid-axis]:!w-8
  [&_.fc-col-header-cell]:!text-xs [&_.fc-col-header-cell]:!py-0.5
  [&_.fc-timegrid-event]:!p-0 [&_.fc-event]:!my-0.5`;

/**
 * Thin wrapper around FullCalendar that owns the shared config and styling.
 * Callers pass their own events, event renderer and handlers.
 */
const BaseCalendar: React.FC<BaseCalendarProps> = ({
  events,
  eventContent,
  onEventClick,
  onDateClick,
  onDatesSet,
  editable = false,
  selectable = false,
  initialView = 'timeGridWeek',
  dayMaxEvents = 3,
  eventMaxStack = 2,
  validRange,
}) => {
  // En móvil la semana completa deja ~65px por columna, donde no cabe ni la hora.
  // Se pasa a tres días: sigue siendo la rejilla — con sus huecos libres y su
  // "pulsa para crear" — pero legible.
  const isMobile = useIsMobile();

  return (
  <div className="bg-white rounded-lg shadow-lg overflow-hidden">
    <div className={`${calendarWrapperClass} ${isMobile ? mobileTweaksClass : ''}`}>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={isMobile && initialView === 'timeGridWeek' ? 'timeGrid3Day' : initialView}
        views={{
          // `dayCount` y no `duration`: con los fines de semana ocultos, tres días
          // de calendario pueden ser dos columnas visibles. Esto cuenta las que se
          // ven.
          timeGrid3Day: { type: 'timeGrid', dayCount: 3, buttonText: '3 días' },
        }}
        validRange={validRange}
        events={events}
        datesSet={onDatesSet}
        dateClick={onDateClick}
        eventClick={onEventClick}
        editable={editable}
        selectable={selectable}
        headerToolbar={isMobile ? {
          left: 'prev,next',
          center: 'title',
          right: 'timeGrid3Day,timeGridDay',
        } : {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        locale={esLocale}
        buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
        slotMinTime="08:00:00"
        slotMaxTime="22:00:00"
        allDaySlot={false}
        dayMaxEvents={dayMaxEvents}
        eventMaxStack={eventMaxStack}
        height="auto"
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        eventContent={eventContent}
        slotEventOverlap={false}
        weekends={false}
      />
    </div>
  </div>
  );
};

export default BaseCalendar;
